import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { ProjectComment } from '../classes/project-comment';
import { ProjectCommentHistory } from '../classes/project-comment-history';
import { ProjectUsedAsphalt } from '../classes/project-used-asphalt.class';
import { Project } from '../classes/project.class';
import { Utils } from '../utils.class';
import { ApiService } from './api/api.service';
import { RealtimeType } from './realtime/realtime-type.enum';
import { RealtimeService } from './realtime/realtime.service';
import { WebsocketService } from './websocket/websocket.service';

@Injectable({
    providedIn: 'root',
})
export class ProjectService extends RealtimeService<Project> {
    constructor(
        private apiService: ApiService,
        protected websocketService: WebsocketService
    ) {
        super(websocketService, RealtimeType.project, 'afas_project_id');
    }

    genPdfOfWeekreports(
        projectId: string,
        onlyTotals = false,
        noHours = false,
        noComments = false,
        noAsphalt = false,
        noCars = true
    ) {
        return this.apiService.getCall$(`project/reports/pdf/${projectId}`, {
            onlyTotals,
            noHours,
            noComments,
            noAsphalt,
            noCars,
        });
    }

    getReports(projectId: string) {
        return this.apiService.getCall$<
            {
                week: number;
                year: number;
                user_minutes: number;
                entity_minutes: number;
                afas_project_id: string;
            }[]
        >(`${this.type}/reports/${projectId}`);
    }

    saveProject(project: Project, saveToAfas: boolean = false) {
        return this.apiService.postCall$<Project>(this.type, { ...project, saveToAfas });
    }

    searchProjects(
        search?: string,
        filters?: { user_id?: number; project_manager_id?: number; contractor?: string; afas_customer_id?: string },
        all?: boolean,
        limit = true
    ) {
        const params: {
            search?: string;
            user_id?: number;
            project_manager_id?: number;
            contractor?: string;
            afas_customer_id?: string;
            all?: boolean;
            limit?: boolean;
        } = {};

        if (search) {
            params.search = search;
        }

        if (filters?.user_id) {
            params.user_id = filters.user_id;
        }

        if (filters?.project_manager_id) {
            params.project_manager_id = filters.project_manager_id;
        }

        if (filters?.contractor) {
            params.contractor = filters.contractor;
        }

        if (filters?.afas_customer_id) {
            params.afas_customer_id = filters.afas_customer_id;
        }

        if (all) {
            params.all = all;
        }

        if (limit === false) {
            params.limit = limit;
        }

        return this.apiService.getCall$<Project[]>(`${this.type}/search`, params).pipe(
            map(projects => {
                if (projects.data?.length) {
                    const mainProjects = projects.data.filter(p => !p.parent_id);
                    mainProjects.forEach(mainProject => {
                        mainProject.projects = projects.data.filter(p => p.parent_id === mainProject.afas_project_id);
                        mainProject.projects.forEach(project => {
                            project.mainProject = mainProject.afas_project_id + ' - ' + mainProject.name;
                        });
                    });
                }
                return projects;
            })
        );
    }

    getRecentProject(userId: number) {
        return this.apiService.getCall$<Project[]>(`${this.type}/recent`, { userId });
    }

    itemInDaterange(project: Project, fromDate: Date, toDate: Date) {
        // Unable to filter, because we don't have all planning_has_entities and user_plannings available
        return true;
    }

    saveProjectComment(projectComment: ProjectComment) {
        return this.apiService.postCall$<ProjectComment>(`${this.type}/comment`, projectComment);
    }

    saveProjectAsphalt(afasProjectId: string, date: Date, projectAsphalt: ProjectUsedAsphalt[]) {
        return this.apiService.postCall$<{}>(`${this.type}/asphalt`, {
            usedAsphalt: projectAsphalt,
            date,
            afasProjectId,
        });
    }

    getProjectAsphalt(afasProjectId: string, date: Date) {
        return this.apiService.getCall$<ProjectUsedAsphalt[]>(`${this.type}/asphalt`, {
            date: Utils.formatDate(date),
            afasProjectId,
        });
    }

    getProject(projectId: string) {
        return this.apiService.getCall$<Project>(`${this.type}/${projectId}`);
    }

    getProjectComment(id: number) {
        return this.apiService.getCall$<ProjectComment>(`${this.type}/comment`, { id });
    }

    getProjectCommentHistory(id: number) {
        return this.apiService.getCall$<ProjectCommentHistory[]>(`${this.type}/comment-history`, { id });
    }

    getProjectCommentsWeek(afasProjectId: string, date: Date, includeWeekComments = false) {
        return this.apiService
            .getCall$<ProjectComment[]>(`${this.type}/comment`, {
                date: Utils.formatDate(date),
                afasProjectId,
            })
            .pipe(
                map(comments => {
                    if (!includeWeekComments) {
                        comments.data = comments.data.filter(c => !c.week);
                    }
                    return comments;
                })
            );
    }
}
